import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { networkLogger, NetworkLogEntry } from '../lib/utils/networkLogger';
import DropboxAdapter from '../lib/sync/dropboxAdapter';
import { csvService } from '../lib/csv/csvService';
import { DisclaimerText } from './DisclaimerText';
import { 
  Settings as SettingsIcon,
  Shield, 
  Download, 
  Upload,
  Moon,
  Sun,
  Monitor,
  Lock,
  Key,
  Database,
  Cloud,
  Network,
  RefreshCw,
  ExternalLink
} from 'lucide-react';

export function Settings() {
  const { state, dispatch, lockLedger, storage } = useApp();
  const [networkLogs, setNetworkLogs] = useState<NetworkLogEntry[]>([]);
  const [dropboxConnected, setDropboxConnected] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error'>('idle');
  
  const dropboxAdapter = DropboxAdapter.getInstance();

  useEffect(() => {
    // Check Dropbox connection status
    setDropboxConnected(dropboxAdapter.isConnected());
    
    // Load initial network logs
    setNetworkLogs(networkLogger.getLogs());
    
    // Set up periodic refresh of logs
    const interval = setInterval(() => {
      setNetworkLogs(networkLogger.getLogs());
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const handleThemeChange = (theme: 'light' | 'dark' | 'system') => {
    dispatch({ type: 'SET_THEME', payload: theme });
  };

  const handleConnectDropbox = async () => {
    try {
      const authUrl = await dropboxAdapter.startAuth();
      window.open(authUrl, '_blank');
    } catch (error) {
      console.error('Failed to start Dropbox auth:', error);
    }
  };

  const handleDisconnectDropbox = () => {
    dropboxAdapter.disconnect();
    setDropboxConnected(false);
  };

  const handleSync = async () => {
    if (!dropboxConnected) return;
    
    setSyncStatus('syncing');
    try {
      const household = storage.getHousehold();
      if (household) {
        // Note: This would need the current ledger ID from storage
        // await dropboxAdapter.syncLedger(ledgerId, household.name);
      }
      setSyncStatus('idle');
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncStatus('error');
    }
  };

  const handleExportLedger = async () => {
    try {
      const jsonData = await storage.exportLedger();
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `budget-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const refreshNetworkLogs = () => {
    setNetworkLogs(networkLogger.getLogs());
  };

  const clearNetworkLogs = () => {
    networkLogger.clearLogs();
    setNetworkLogs([]);
  };

  // CSV Export functions
  const handleExportTransactionsCSV = () => {
    const transactions = storage.getTransactions();
    const accounts = storage.getAccounts();
    const categories = storage.getCategories();
    const csvData = csvService.exportTransactions(transactions, accounts, categories);
    csvService.downloadCSV(csvData, `transactions-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleExportAccountsCSV = () => {
    const accounts = storage.getAccounts();
    const csvData = csvService.exportAccounts(accounts);
    csvService.downloadCSV(csvData, `accounts-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleExportCategoriesCSV = () => {
    const categories = storage.getCategories();
    const csvData = csvService.exportCategories(categories);
    csvService.downloadCSV(csvData, `categories-${new Date().toISOString().split('T')[0]}.csv`);
  };

  return (
    <div className="py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground">Manage your PrivyBudget preferences</p>
        </div>
      </div>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Security & Privacy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Auto-lock timeout</Label>
              <p className="text-sm text-muted-foreground">
                Automatically lock the app after inactivity
              </p>
            </div>
            <Switch defaultChecked />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Biometric unlock</Label>
              <p className="text-sm text-muted-foreground">
                Use fingerprint or face recognition (if available)
              </p>
            </div>
            <Switch />
          </div>

          <div className="pt-4 border-t">
            <Button variant="outline" className="w-full" onClick={lockLedger}>
              <Lock className="w-4 h-4 mr-2" />
              Lock Ledger Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <SettingsIcon className="w-5 h-5 mr-2" />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-base font-medium mb-3 block">Theme</Label>
            <div className="flex space-x-2">
              <Button
                variant={state.theme === 'light' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleThemeChange('light')}
              >
                <Sun className="w-4 h-4 mr-2" />
                Light
              </Button>
              <Button
                variant={state.theme === 'dark' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleThemeChange('dark')}
              >
                <Moon className="w-4 h-4 mr-2" />
                Dark
              </Button>
              <Button
                variant={state.theme === 'system' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleThemeChange('system')}
              >
                <Monitor className="w-4 h-4 mr-2" />
                System
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="w-5 h-5 mr-2" />
            Data Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button variant="outline" className="flex-col h-20" onClick={handleExportLedger}>
              <Download className="w-5 h-5 mb-2" />
              <span>Export Data</span>
              <span className="text-xs text-muted-foreground">Download encrypted backup</span>
            </Button>
            
            <Button variant="outline" className="flex-col h-20">
              <Upload className="w-5 h-5 mb-2" />
              <span>Import Data</span>
              <span className="text-xs text-muted-foreground">Restore from backup</span>
            </Button>
          </div>

          <div className="pt-4 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Auto-backup</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically create encrypted snapshots
                </p>
              </div>
              <Switch defaultChecked />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* CSV Import/Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Download className="w-5 h-5 mr-2" />
            CSV Import/Export
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button variant="outline" onClick={handleExportTransactionsCSV} className="flex-col h-16">
              <Download className="w-4 h-4 mb-1" />
              <span className="text-sm">Export Transactions</span>
            </Button>
            
            <Button variant="outline" onClick={handleExportAccountsCSV} className="flex-col h-16">
              <Download className="w-4 h-4 mb-1" />
              <span className="text-sm">Export Accounts</span>
            </Button>
            
            <Button variant="outline" onClick={handleExportCategoriesCSV} className="flex-col h-16">
              <Download className="w-4 h-4 mb-1" />
              <span className="text-sm">Export Categories</span>
            </Button>
          </div>
          
          <div className="pt-3 border-t">
            <div className="text-center">
              <Button variant="outline" disabled className="flex items-center">
                <Upload className="w-4 h-4 mr-2" />
                Import CSV (Coming Soon)
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                CSV import functionality will be available in a future update
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dropbox Sync */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Cloud className="w-5 h-5 mr-2" />
            Dropbox Sync
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Connection Status</Label>
              <p className="text-sm text-muted-foreground">
                {dropboxConnected 
                  ? "Connected to Dropbox - encrypted backups enabled"
                  : "Not connected - local storage only"
                }
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${dropboxConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-sm">{dropboxConnected ? 'Connected' : 'Disconnected'}</span>
            </div>
          </div>
          
          <div className="flex space-x-2">
            {dropboxConnected ? (
              <>
                <Button 
                  onClick={handleSync} 
                  disabled={syncStatus === 'syncing'}
                  className="flex items-center"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
                  {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
                </Button>
                <Button variant="outline" onClick={handleDisconnectDropbox}>
                  Disconnect
                </Button>
              </>
            ) : (
              <Button onClick={handleConnectDropbox}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Connect Dropbox
              </Button>
            )}
          </div>
          
          {syncStatus === 'error' && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">Sync failed. Please try again.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Network Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Network className="w-5 h-5 mr-2" />
              Network Log
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" onClick={refreshNetworkLogs}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={clearNetworkLogs}>
                Clear
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {networkLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No network requests logged yet</p>
            ) : (
              networkLogs.map((log, index) => (
                <div 
                  key={index} 
                  className={`p-2 rounded border text-sm ${
                    log.blocked ? 'bg-destructive/10 border-destructive/20' : 'bg-muted/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        log.blocked ? 'bg-destructive text-destructive-foreground' : 'bg-green-100 text-green-800'
                      }`}>
                        {log.blocked ? 'BLOCKED' : 'ALLOWED'}
                      </span>
                      <span className="font-mono text-xs">{log.method}</span>
                      <span className="text-xs">{log.host}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 font-mono truncate">
                    {log.url}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Legal / About */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Legal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold mb-3">Disclaimer</h4>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-3 bg-muted/30">
                <DisclaimerText />
              </div>
            </div>
            
            <div className="pt-3 border-t">
              <p className="text-xs text-muted-foreground text-center">
                By using this application, you acknowledge that you have read and accepted these terms.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Household Info */}
      <Card>
        <CardHeader>
          <CardTitle>Household Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium">Household Name:</span>
              <span className="text-sm">{state.auth.household?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Your Role:</span>
              <span className="text-sm capitalize">{state.auth.currentUser?.role}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Members:</span>
              <span className="text-sm">{state.auth.household?.members.length || 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}